/** @file Multi-sheet packing on image-io's single-sheet packShelves; export owns it (R4). */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { PackInput, PackOptions, PackResult, PackShelves } from '@zilo/pose3d-export/types/imageIoShapes';

/** One packed sheet: its size (padding included) and the rects on it. */
export type PackedSheet = { width: number; height: number; rects: PackResult['rects'] };

/** packShelves' stable order: height desc, width desc, id asc. */
const order = (a: PackInput, b: PackInput): number => b.h - a.h || b.w - a.w || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** `packShelves(items, options)`, or undefined when the packer throws (e.g. image-io `PACK_TOO_WIDE`). */
function attempt(packShelves: PackShelves, items: PackInput[], options: PackOptions): PackResult | undefined {
  try {
    return packShelves(items, options);
  } catch {
    return undefined;
  }
}

/** The packing of `items` on one sheet within `maxSide`, or undefined: tries the packer's own width, then `maxSide`. */
function fit(items: PackInput[], maxSide: number, padding: number, packShelves: PackShelves): PackResult | undefined {
  const inside = (r: PackResult | undefined) => r !== undefined && r.width <= maxSide && r.height <= maxSide;
  const free = attempt(packShelves, items, { padding });
  if (inside(free)) return free;
  const bounded = attempt(packShelves, items, { padding, maxWidth: maxSide });
  return inside(bounded) ? bounded : undefined;
}

function tooLarge(item: PackInput, maxSide: number): never {
  return throwError(ERROR_CODES.LIMIT, { limit: 'sheetSide', max: maxSide, id: item.id, size: [item.w, item.h] }, {
    hint: `blocks must fit in ${maxSide} px with padding` });
}

const sheetOf = (r: PackResult): PackedSheet => ({ width: r.width, height: r.height, rects: r.rects });

/**
 * Packs `items` onto as few sheets as the greedy rule gives: in packShelves' order each item joins the current sheet
 * while the packed result stays within `maxSide` on both sides, else a new sheet starts. An item that alone (with
 * padding) exceeds `maxSide`, or that the packer cannot fit alone within it, throws `LIMIT` `{ limit: 'sheetSide' }`; a
 * packer that throws counts as "does not fit". The server imports it (R4).
 */
export function packSheets(items: PackInput[], opts: { maxSide: number; padding: number }, packShelves: PackShelves): { sheets: PackedSheet[] } {
  const { maxSide, padding } = opts;
  const sheets: PackedSheet[] = [];
  let current: PackInput[] = [];
  let packed: PackResult | undefined;
  for (const item of [...items].sort(order)) {
    if (item.w + 2 * padding > maxSide || item.h + 2 * padding > maxSide) tooLarge(item, maxSide);
    const next = fit([...current, item], maxSide, padding, packShelves);
    if (next !== undefined) { current.push(item); packed = next; continue; }
    if (packed !== undefined) sheets.push(sheetOf(packed));
    current = [item];
    packed = fit(current, maxSide, padding, packShelves) ?? tooLarge(item, maxSide);
  }
  if (packed !== undefined) sheets.push(sheetOf(packed));
  return { sheets };
}
